import { useState } from 'react';
import AppLayout from '@/components/feature/AppLayout';
import { mockAiTasks, mockAiStats } from '@/mocks/aiTasks';

const typeLabels: Record<string, string> = {
  titulo: 'Título',
  resumo: 'Resumo',
  classificacao: 'Classificação',
  imagem: 'Imagem',
  layout: 'Layout',
};
const typeIcons: Record<string, string> = {
  titulo: 'ri-text',
  resumo: 'ri-file-text-line',
  classificacao: 'ri-price-tag-3-line',
  imagem: 'ri-image-line',
  layout: 'ri-layout-grid-line',
};

export default function AIProcessingPage() {
  const [tasks, setTasks] = useState(mockAiTasks);
  const [activeHistoryFilter, setActiveHistoryFilter] = useState('todos');

  const handleApprove = (id: string) =>
    setTasks((prev) => prev.map((t) => (t.id === id ? { ...t, status: 'aprovado' } : t)));
  const handleReject = (id: string) =>
    setTasks((prev) => prev.map((t) => (t.id === id ? { ...t, status: 'rejeitado' } : t)));

  const pending = tasks.filter((t) => t.status === 'pendente_revisao');
  const processing = tasks.filter((t) => t.status === 'processando');
  const done = tasks.filter((t) => ['concluido', 'aprovado', 'rejeitado'].includes(t.status));

  const historyFilters = ['todos', 'aprovado', 'rejeitado', 'concluido'];
  const filteredHistory = activeHistoryFilter === 'todos'
    ? done
    : done.filter((t) => t.status === activeHistoryFilter);

  return (
    <AppLayout>
      <div className="p-6 space-y-5">

        {/* Stats Row — WaPo style */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-0 border border-[#E0E0E0] divide-x divide-[#E0E0E0]">
          {[
            { icon: 'ri-cpu-line', value: mockAiStats.activeNow, label: 'Processando Agora', accent: true },
            { icon: 'ri-time-line', value: mockAiStats.pendingReview, label: 'Aguardando Revisão', accent: false },
            { icon: 'ri-check-line', value: mockAiStats.totalProcessed, label: 'Total Processado', accent: false },
            { icon: 'ri-percent-line', value: `${mockAiStats.avgConfidence}%`, label: 'Confiança Média', accent: false },
          ].map((s, i) => (
            <div
              key={i}
              className={`bg-white p-5 border-t-2 ${s.accent ? 'border-t-[#C41230]' : 'border-t-[#111111]'}`}
            >
              <div className="flex items-center gap-2 mb-3">
                <div className="w-6 h-6 flex items-center justify-center">
                  <i className={`${s.icon} text-[#444444] text-sm`}></i>
                </div>
                {s.accent && (
                  <span className="w-1.5 h-1.5 rounded-full bg-[#C41230] animate-pulse"></span>
                )}
              </div>
              <p className="text-3xl font-bold text-[#111111] font-serif">{s.value}</p>
              <p className="text-[10px] text-[#666666] font-sans uppercase tracking-wider mt-1">{s.label}</p>
            </div>
          ))}
        </div>

        {/* Main Grid */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">

          {/* Processing Now */}
          <div className="bg-white border border-[#E0E0E0] border-t-2 border-t-[#111111]">
            <div className="px-5 py-3 border-b border-[#E0E0E0] flex items-center gap-2">
              <span className="w-1.5 h-1.5 rounded-full bg-[#C41230] animate-pulse"></span>
              <h2 className="text-xs font-bold text-[#111111] font-serif uppercase tracking-wide">Processando Agora</h2>
            </div>
            <div className="p-4">
              {processing.length === 0 ? (
                <div className="text-center py-10">
                  <div className="w-10 h-10 flex items-center justify-center mx-auto mb-2 border border-[#E0E0E0]">
                    <i className="ri-cpu-line text-[#C8C8C8] text-lg"></i>
                  </div>
                  <p className="text-xs text-[#999999] font-sans">Nenhuma tarefa ativa</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {processing.map((t) => (
                    <div key={t.id} className="p-3 border border-[#E0E0E0] border-l-2 border-l-[#111111]">
                      <div className="flex items-center gap-2 mb-2">
                        <div className="w-5 h-5 flex items-center justify-center">
                          <i className={`${typeIcons[t.type]} text-[#444444] text-xs`}></i>
                        </div>
                        <span className="text-[10px] font-bold text-[#111111] uppercase tracking-wide font-sans">{typeLabels[t.type]}</span>
                      </div>
                      <p className="text-xs text-[#444444] font-sans mb-2 line-clamp-1">{t.articleTitle}</p>
                      <div className="w-full bg-[#F0F0F0] h-1">
                        <div
                          className="bg-[#111111] h-1 transition-all"
                          style={{ width: `${t.progress || 0}%` }}
                        ></div>
                      </div>
                      <p className="text-[10px] text-[#999999] mt-1 font-sans">{t.progress || 0}% concluído</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Pending Review */}
          <div className="bg-white border border-[#E0E0E0] border-t-2 border-t-[#C41230] lg:col-span-2">
            <div className="px-5 py-3 border-b border-[#E0E0E0] flex items-center justify-between">
              <h2 className="text-xs font-bold text-[#111111] font-serif uppercase tracking-wide">Sugestões Pendentes de Revisão</h2>
              <span className="text-[10px] font-bold text-[#C41230] border border-[#C41230] px-2 py-0.5 font-sans">{pending.length} pendentes</span>
            </div>
            <div className="p-4">
              {pending.length === 0 ? (
                <div className="text-center py-10">
                  <div className="w-10 h-10 flex items-center justify-center mx-auto mb-2 border border-[#E0E0E0]">
                    <i className="ri-check-double-line text-[#1a7a1a] text-lg"></i>
                  </div>
                  <p className="text-xs text-[#999999] font-sans">Nenhuma sugestão pendente</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {pending.map((t) => (
                    <div key={t.id} className="border border-[#E0E0E0] hover:border-[#111111] transition-colors">
                      {/* Card Header */}
                      <div className="flex items-center justify-between px-4 py-2.5 border-b border-[#F0F0F0] bg-[#F8F8F8]">
                        <div className="flex items-center gap-2">
                          <div className="w-5 h-5 flex items-center justify-center">
                            <i className={`${typeIcons[t.type]} text-[#444444] text-xs`}></i>
                          </div>
                          <span className="text-[10px] font-bold text-[#111111] uppercase tracking-wide font-sans">{typeLabels[t.type]}</span>
                          <span className="text-[#C8C8C8]">·</span>
                          <span className="text-[10px] text-[#666666] font-sans line-clamp-1 max-w-48">{t.articleTitle}</span>
                        </div>
                        <div className="flex items-center gap-1.5">
                          <div className="w-14 bg-[#F0F0F0] h-1">
                            <div className="bg-[#1a7a1a] h-1" style={{ width: `${t.confidence}%` }}></div>
                          </div>
                          <span className="text-[10px] text-[#666666] font-sans font-semibold">{t.confidence}%</span>
                        </div>
                      </div>

                      {/* Comparison */}
                      <div className="p-4">
                        {t.type === 'imagem' ? (
                          <div className="grid grid-cols-2 gap-3 mb-3">
                            <div>
                              <p className="text-[9px] font-bold text-[#999999] uppercase tracking-wider mb-1.5 font-sans">Original</p>
                              <div className="h-24 bg-[#F8F8F8] border border-[#E0E0E0] flex items-center justify-center">
                                <i className="ri-image-line text-[#C8C8C8] text-2xl"></i>
                              </div>
                            </div>
                            <div>
                              <p className="text-[9px] font-bold text-[#C41230] uppercase tracking-wider mb-1.5 font-sans">Sugestão IA</p>
                              <div className="h-24 overflow-hidden border border-[#E0E0E0]">
                                <img src={t.suggestion} alt="" className="w-full h-full object-cover object-top" />
                              </div>
                            </div>
                          </div>
                        ) : (
                          <div className="grid grid-cols-2 gap-3 mb-3">
                            <div className="p-3 bg-[#F8F8F8] border border-[#E0E0E0]">
                              <p className="text-[9px] font-bold text-[#999999] uppercase tracking-wider mb-1.5 font-sans">Original</p>
                              <p className="text-xs text-[#444444] font-source-serif leading-relaxed">{t.original}</p>
                            </div>
                            <div className="p-3 bg-white border-2 border-[#111111]">
                              <p className="text-[9px] font-bold text-[#C41230] uppercase tracking-wider mb-1.5 font-sans">Sugestão IA</p>
                              <p className="text-xs text-[#111111] font-serif font-bold leading-tight">{t.suggestion}</p>
                            </div>
                          </div>
                        )}

                        {/* Actions */}
                        <div className="flex items-center gap-0 border border-[#E0E0E0]">
                          <button
                            onClick={() => handleApprove(t.id)}
                            className="flex-1 py-2 text-[10px] font-bold text-white bg-[#1a7a1a] hover:bg-[#155e15] cursor-pointer font-sans uppercase tracking-wide whitespace-nowrap transition-colors flex items-center justify-center gap-1"
                          >
                            <i className="ri-check-line text-xs"></i>Aprovar
                          </button>
                          <button className="flex-1 py-2 text-[10px] font-bold text-[#444444] bg-white hover:bg-[#F0F0F0] border-x border-[#E0E0E0] cursor-pointer font-sans uppercase tracking-wide whitespace-nowrap transition-colors flex items-center justify-center gap-1">
                            <i className="ri-edit-line text-xs"></i>Editar
                          </button>
                          <button
                            onClick={() => handleReject(t.id)}
                            className="flex-1 py-2 text-[10px] font-bold text-white bg-[#C41230] hover:bg-[#a00e26] cursor-pointer font-sans uppercase tracking-wide whitespace-nowrap transition-colors flex items-center justify-center gap-1"
                          >
                            <i className="ri-close-line text-xs"></i>Rejeitar
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* History Table */}
        <div className="bg-white border border-[#E0E0E0]">
          <div className="px-5 py-3 border-b-2 border-[#111111] flex items-center justify-between">
            <h2 className="text-xs font-bold text-[#111111] font-serif uppercase tracking-wide">Histórico de Processamentos</h2>
            {/* Filter chips */}
            <div className="flex items-center gap-0 border border-[#E0E0E0]">
              {historyFilters.map((f) => (
                <button
                  key={f}
                  onClick={() => setActiveHistoryFilter(f)}
                  className={`px-3 py-1 text-[10px] font-bold uppercase tracking-wide font-sans cursor-pointer whitespace-nowrap transition-colors ${
                    activeHistoryFilter === f
                      ? 'bg-[#111111] text-white'
                      : 'bg-white text-[#666666] hover:bg-[#F0F0F0]'
                  }`}
                >
                  {f}
                </button>
              ))}
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-[#E0E0E0] bg-[#F8F8F8]">
                  {['Tipo', 'Artigo', 'Status', 'Confiança', 'Duração', 'Data'].map((h) => (
                    <th key={h} className="text-left text-[10px] font-bold text-[#111111] uppercase tracking-wider px-4 py-2.5 font-sans">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filteredHistory.map((t, idx) => (
                  <tr
                    key={t.id}
                    className={`border-b border-[#F0F0F0] hover:bg-[#F8F8F8] transition-colors ${idx % 2 !== 0 ? 'bg-[#FAFAFA]' : ''}`}
                  >
                    <td className="px-4 py-2.5">
                      <div className="flex items-center gap-2">
                        <div className="w-5 h-5 flex items-center justify-center">
                          <i className={`${typeIcons[t.type]} text-[#666666] text-xs`}></i>
                        </div>
                        <span className="text-xs text-[#444444] font-sans">{typeLabels[t.type]}</span>
                      </div>
                    </td>
                    <td className="px-4 py-2.5">
                      <span className="text-xs text-[#444444] font-sans line-clamp-1 max-w-xs">{t.articleTitle}</span>
                    </td>
                    <td className="px-4 py-2.5">
                      <span className={`text-[9px] font-bold border px-2 py-0.5 font-sans uppercase tracking-wide ${
                        t.status === 'aprovado'
                          ? 'border-[#1a7a1a] text-[#1a7a1a]'
                          : t.status === 'rejeitado'
                          ? 'border-[#C41230] text-[#C41230]'
                          : 'border-[#999999] text-[#666666]'
                      }`}>
                        {t.status}
                      </span>
                    </td>
                    <td className="px-4 py-2.5">
                      <div className="flex items-center gap-2">
                        <div className="w-12 bg-[#F0F0F0] h-1">
                          <div className="bg-[#1a7a1a] h-1" style={{ width: `${t.confidence}%` }}></div>
                        </div>
                        <span className="text-xs text-[#666666] font-sans">{t.confidence}%</span>
                      </div>
                    </td>
                    <td className="px-4 py-2.5">
                      <span className="text-xs text-[#999999] font-sans">{t.duration || '—'}</span>
                    </td>
                    <td className="px-4 py-2.5">
                      <span className="text-xs text-[#999999] font-sans">
                        {new Date(t.createdAt).toLocaleString('pt-BR')}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </AppLayout>
  );
}
